/** How long past its own end an exit animation may run before it is given up. */
const EXIT_SLACK_MS = 1000;

interface AfterExitOptions {
   /** Wait for the element's pseudo-elements too: a dialog's ::backdrop. */
   pseudoElements?: boolean;
}

/**
 * Runs `done` once the element's exit animations have ended. Without a running
 * animation (reduced motion, a test DOM) that is at once, and a stuck one is
 * given up a second after it should have ended, so nothing can linger closed.
 * The returned function disarms the run, for an element that reopens mid-exit:
 * a cancelled animation still settles.
 */
export function afterExit(
   element: Element | null,
   done: () => void,
   { pseudoElements = false }: AfterExitOptions = {}
): () => void {
   const animations = ownAnimations(element, pseudoElements);
   if (animations.length === 0) {
      done();
      return () => {};
   }
   let isDisarmed = false;
   const finish = () => {
      if (isDisarmed) return;
      isDisarmed = true;
      done();
   };
   void (async () => {
      await Promise.allSettled(animations.map(animation => animation.finished));
      finish();
   })();
   const fallback = setTimeout(finish, longestRemainingMs(animations) + EXIT_SLACK_MS);
   return () => {
      isDisarmed = true;
      clearTimeout(fallback);
   };
}

// The subtree also holds the children's animations, a spinner's that never
// ends among them; the element's pseudo-elements are the ones it targets.
function ownAnimations(element: Element | null, pseudoElements: boolean) {
   if (!element?.getAnimations) return [];
   if (!pseudoElements) return element.getAnimations();
   return element
      .getAnimations({ subtree: true })
      .filter(animation => (animation.effect as KeyframeEffect | null)?.target === element);
}

// A long exit, like the newsletter popup's 2s fade, plays out in full. An
// endless animation has no end to wait for.
function longestRemainingMs(animations: Animation[]) {
   return Math.max(
      0,
      ...animations.map(animation => {
         if (!animation.effect) return 0;
         const endTime = Number(animation.effect.getComputedTiming().endTime);
         return Number.isFinite(endTime) ? endTime - Number(animation.currentTime ?? 0) : 0;
      })
   );
}
